import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { Card, CardBody } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type NotifRow = {
  ID: number;
  TITLE: string | null;
  CONTENT: string | null;
  DATA: string | null;
  IS_READ: number | null;
  CREATED_DATE: string | null;
};

type Filter = "all" | "unread" | "read";

type ListResp = { items: NotifRow[]; total: number; page: number; pageSize: number };
type UnreadResp = { unread: number };

function unwrap<T>(payload: unknown): T {
  const obj = payload as { data?: T } | T;
  if (obj && typeof obj === "object" && "data" in (obj as Record<string, unknown>)) {
    return (obj as { data: T }).data;
  }
  return obj as T;
}

function routeForData(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as { type?: string; object_id?: number | string };
    const id = d.object_id;
    if (id == null) return null;
    switch (d.type) {
      case "PIOFFICE_1":
        return `/documents/${id}`;
      case "PIOFFICE_4":
        return `/signs/${id}`;
      case "PIOFFICE_3":
        return null; // no meeting detail route yet
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export function NotificationsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Reset page when filter changes
  const setFilterAndReset = (f: Filter) => {
    setFilter(f);
    setPage(1);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["notification", "list", filter, page],
    queryFn: async () => {
      const { data } = await api.get(
        `/notification/list?page=${page}&pageSize=${pageSize}&filter=${filter}`,
      );
      return unwrap<ListResp>(data);
    },
  });

  const { data: unreadData } = useQuery({
    queryKey: ["notification", "unread"],
    queryFn: async () => {
      const { data } = await api.get("/notification/unread-count");
      return unwrap<UnreadResp>(data);
    },
    refetchInterval: 60_000,
  });

  const markReadMut = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/notification/${id}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification"] });
    },
  });

  const markAllMut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/notification/read-all");
      return unwrap<{ markedRead: number }>(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification"] });
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const unread = unreadData?.unread ?? 0;

  const emptyText = useMemo(() => {
    switch (filter) {
      case "unread":
        return "Không có thông báo chưa đọc";
      case "read":
        return "Chưa có thông báo đã đọc";
      default:
        return "Chưa có thông báo";
    }
  }, [filter]);

  function handleClick(n: NotifRow) {
    const target = routeForData(n.DATA);
    if ((n.IS_READ ?? 0) === 0) {
      markReadMut.mutate(n.ID);
    }
    if (target) nav(target);
  }

  const tabs: { key: Filter; label: string; badge?: number }[] = [
    { key: "all", label: "Tất cả" },
    { key: "unread", label: "Chưa đọc", badge: unread },
    { key: "read", label: "Đã đọc" },
  ];

  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  return (
    <div>
      <PageHeader
        title="Thông báo"
        subtitle={
          total > 0
            ? `Hiển thị ${rangeFrom}–${rangeTo} trong ${total.toLocaleString("vi-VN")} thông báo · ${unread.toLocaleString("vi-VN")} chưa đọc`
            : `${unread.toLocaleString("vi-VN")} chưa đọc`
        }
        breadcrumb="Thông báo"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllMut.mutate()}
            disabled={unread === 0 || markAllMut.isPending}
            className="gap-2"
          >
            <CheckCheck className="h-4 w-4" />
            Đánh dấu tất cả đã đọc
          </Button>
        }
      />

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 border-b border-border">
        {tabs.map((t) => {
          const active = filter === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setFilterAndReset(t.key)}
              className={cn(
                "relative h-10 px-4 text-sm font-medium transition-colors inline-flex items-center gap-2",
                active ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span>{t.label}</span>
              {t.badge !== undefined && t.badge > 0 && (
                <span
                  className={cn(
                    "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xxs font-semibold tabular-nums",
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {t.badge > 99 ? "99+" : t.badge}
                </span>
              )}
              {active && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary" />}
            </button>
          );
        })}
      </div>

      <Card>
        <CardBody className="p-0">
          {isLoading && (
            <div className="p-10 text-center text-muted-foreground">
              <span className="inline-block h-3 w-3 rounded-full bg-primary/60 animate-pulse mr-2 align-middle" />
              Đang tải…
            </div>
          )}
          {!isLoading && items.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">{emptyText}</div>
          )}
          <ul className="divide-y divide-border">
            {items.map((n) => {
              const isUnread = (n.IS_READ ?? 0) === 0;
              const target = routeForData(n.DATA);
              const clickable = isUnread || !!target;
              return (
                <li
                  key={n.ID}
                  onClick={clickable ? () => handleClick(n) : undefined}
                  className={cn(
                    "flex items-start gap-3 px-5 py-3 transition-colors",
                    isUnread && "bg-primary-50/40",
                    clickable && "cursor-pointer hover:bg-muted/40",
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 h-9 w-9 shrink-0 rounded-md grid place-items-center",
                      isUnread
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    <Bell className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-sm truncate">{n.TITLE ?? "Thông báo"}</span>
                      {isUnread && <StatusBadge variant="primary">Mới</StatusBadge>}
                    </div>
                    <div className="text-sm text-muted-foreground line-clamp-2">
                      {n.CONTENT ?? "—"}
                    </div>
                    <div className="text-xxs text-muted-foreground/70 mt-1 tabular-nums">
                      {n.CREATED_DATE ?? ""}
                    </div>
                  </div>
                  {isUnread && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markReadMut.mutate(n.ID);
                      }}
                      className="h-7 px-2 text-xs rounded hover:bg-muted text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    >
                      <Check className="h-3.5 w-3.5" /> Đọc
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </CardBody>
      </Card>

      {total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
